/**
 * Participant UI strings. English is the base; the survey's locale picks the language
 * (IDEA.md: Language). Missing keys in a translation fall back to English.
 */
const en = {
  loading: 'Loading…',
  notFound: 'This link does not work. Check that you copied all of it.',
  closed: 'This survey is closed and no longer accepts answers.',
  noSession: 'Open the link from your invitation to start.',
  resumeInvalid: 'That continue link no longer works.',
  start: 'Start',
  continue: 'Continue',
  password: 'Password',
  passwordWrong: 'Wrong password.',
  'privacy.anonymous': 'Anonymous',
  'privacy.confidential': 'Confidential',
  'privacy.identified': 'Identified',
  trustShort: 'Privacy',
  trustLinkFull: 'Read how your answers are protected, who sees what and when everything is deleted',
  trustTitle: 'Privacy and trust',
  trustIntro: 'This page is generated from the survey’s settings, which were fixed when it opened. Every protection marked with a tick is checked by an automated test.',
  claimTested: 'Checked by an automated test:',
  language: 'Language',
  contactHeading: 'Questions',
  contactText: 'Questions about the survey or your answers? Contact {name}:',
  subprocessors: 'Sub-processors',
  subprocessorsIntro: 'Companies that handle data for this survey.',
  subprocessorName: 'Company',
  subprocessorRole: 'Handles',
  subprocessorLocation: 'Where',
  rateLimited: 'Too many attempts from your network right now. Try again in a while.',
  'error.rate_limited': 'Too many requests from your network right now. Your answer is still here — try again in a while.',
  progressName: 'How far you have come',
  progressStart: 'Just started',
  progressEarly: 'A little way in',
  progressHalf: 'About halfway',
  progressPastHalf: 'More than halfway',
  progressNearly: 'Nearly done',
  progressDone: 'Done',
  conversation: 'Conversation',
  interviewer: 'Interviewer:',
  you: 'You:',
  writing: 'Writing…',
  summarising: 'Summarising what you said…',
  answerLabel: 'Your answer',
  answerPlaceholder: 'Write your answer…',
  send: 'Send',
  pause: 'Pause',
  pauseTitle: 'Continue later',
  pausePersonal: 'Open your personal link again to continue where you left off, on any device.',
  pauseShared: 'Save your continue link. It takes you back here, on any device.',
  pauseLost: 'You can continue in this browser. The continue link was only shown when you started.',
  copyLink: 'Copy continue link',
  copied: 'Copied',
  close: 'Close',
  checkpointLabel: 'Checkpoint',
  checkpointTitle: 'Have we understood you correctly?',
  checkpointIntro: 'This is what we take with us from this part. Correct it if needed — this summary is what is saved.',
  confirm: 'Yes, that is right — continue',
  correct: 'Correct the summary',
  correctionLabel: 'What should change?',
  sendCorrection: 'Send correction',
  cancel: 'Cancel',
  doneTitle: 'Thank you!',
  doneBody: 'Your answers are saved. You can close this window.',
  stoppedTitle: 'You have ended the survey',
  stoppedBody: 'What you answered so far is saved. You can close this window.',
  stop: 'End here',
  stopConfirm: 'End the survey here? What you have answered so far is saved.',
  stopYes: 'Yes, end it',
  stopNo: 'No, continue',
  'error.busy': 'Wait a moment, your previous answer is still being processed.',
  'error.unavailable': 'The AI service is not responding right now. Your answer is still here — try again.',
  'error.conflict': 'The page was out of date and has been refreshed.',
  'error.closed': 'This survey is closed and no longer accepts answers.',
  'error.generic': 'Something went wrong. Try again.',
  backgroundTitle: 'About you (optional)',
  backgroundHelp: 'Used only to show results per group. Groups too small to protect anyone are never shown.',
  preferNot: 'Prefer not to say',
  pulseSubmit: 'Send answers',
  skipPage: 'Skip',
  back: 'Back',
  declineConversation: 'I would rather not talk about this',
  measureSkipped: 'Skipped',
  measureReopened: 'Your answer now is selected. Change it if you like, then send.',
  quickReplies: 'Suggested replies',
  quickRepliesHint: 'Choose a reply or write in your own words.',
  followUpTitle: 'A follow-up question',
  followUpContext: 'About “{question}”: you answered {answer}.',
  changeAnswer: 'Change answer',
  roundLabel: 'Round {n} · {when}',
  doneTrust: 'How your answers are stored and protected',
  skip: 'Skip',
  textAnswer: 'Your answer (optional)',
  textPlaceholder: 'Write here…',
};
export type Key = keyof typeof en;

const sv: Partial<Record<Key, string>> = {
  loading: 'Laddar…',
  notFound: 'Länken fungerar inte. Kontrollera att du fått med hela.',
  closed: 'Undersökningen är stängd och tar inte emot fler svar.',
  noSession: 'Öppna länken i din inbjudan för att börja.',
  resumeInvalid: 'Fortsättningslänken fungerar inte längre.',
  start: 'Starta',
  continue: 'Fortsätt',
  password: 'Lösenord',
  passwordWrong: 'Fel lösenord.',
  'privacy.anonymous': 'Anonymt',
  'privacy.confidential': 'Konfidentiellt',
  'privacy.identified': 'Identifierat',
  trustShort: 'Integritet',
  trustLinkFull: 'Läs hur dina svar skyddas, vem som ser vad och när allt raderas',
  trustTitle: 'Integritet och förtroende',
  trustIntro: 'Sidan skapas från undersökningens inställningar, som låstes när den öppnade. Varje skydd som är markerat med en bock kontrolleras av ett automatiskt test.',
  claimTested: 'Kontrolleras av ett automatiskt test:',
  language: 'Språk',
  contactHeading: 'Frågor',
  contactText: 'Frågor om undersökningen eller dina svar? Kontakta {name}:',
  subprocessors: 'Underbiträden',
  subprocessorsIntro: 'Företag som hanterar uppgifter för undersökningen.',
  subprocessorName: 'Företag',
  subprocessorRole: 'Hanterar',
  subprocessorLocation: 'Var',
  rateLimited: 'För många försök från ditt nätverk just nu. Försök igen om en stund.',
  'error.rate_limited': 'För många anrop från ditt nätverk just nu. Ditt svar finns kvar — försök igen om en stund.',
  progressName: 'Hur långt du har kommit',
  progressStart: 'Precis börjat',
  progressEarly: 'En bit in',
  progressHalf: 'Ungefär halvvägs',
  progressPastHalf: 'Mer än halvvägs',
  progressNearly: 'Nästan klart',
  progressDone: 'Klart',
  conversation: 'Samtal',
  interviewer: 'Intervjuare:',
  you: 'Du:',
  writing: 'Skriver…',
  summarising: 'Sammanfattar det du har sagt…',
  answerLabel: 'Ditt svar',
  answerPlaceholder: 'Skriv ditt svar…',
  send: 'Skicka',
  pause: 'Pausa',
  pauseTitle: 'Fortsätt senare',
  pausePersonal: 'Öppna din personliga länk igen för att fortsätta där du slutade, på vilken enhet som helst.',
  pauseShared: 'Spara din fortsättningslänk. Den tar dig tillbaka hit, på vilken enhet som helst.',
  pauseLost: 'Du kan fortsätta i den här webbläsaren. Fortsättningslänken visades bara när du startade.',
  copyLink: 'Kopiera fortsättningslänk',
  copied: 'Kopierad',
  close: 'Stäng',
  checkpointLabel: 'Kontrollpunkt',
  checkpointTitle: 'Har vi förstått dig rätt?',
  checkpointIntro: 'Det här är vad vi tar med oss från delen. Rätta gärna – det är den här sammanfattningen som sparas.',
  confirm: 'Ja, det stämmer – gå vidare',
  correct: 'Rätta sammanfattningen',
  correctionLabel: 'Vad ska ändras?',
  sendCorrection: 'Skicka rättelse',
  cancel: 'Avbryt',
  doneTitle: 'Tack!',
  doneBody: 'Dina svar är sparade. Du kan stänga fönstret.',
  stoppedTitle: 'Du har avslutat undersökningen',
  stoppedBody: 'Det du har svarat hittills är sparat. Du kan stänga fönstret.',
  stop: 'Avsluta här',
  stopConfirm: 'Vill du avsluta undersökningen här? Det du har svarat hittills sparas.',
  stopYes: 'Ja, avsluta',
  stopNo: 'Nej, fortsätt',
  'error.busy': 'Vänta lite, ditt förra svar behandlas fortfarande.',
  'error.unavailable': 'AI-tjänsten svarar inte just nu. Ditt svar finns kvar – försök igen.',
  'error.conflict': 'Sidan var inaktuell och har uppdaterats.',
  'error.closed': 'Undersökningen är stängd och tar inte emot fler svar.',
  'error.generic': 'Något gick fel. Försök igen.',
  backgroundTitle: 'Om dig (frivilligt)',
  backgroundHelp: 'Används bara för att visa resultat per grupp. Grupper som är för små för att skydda någon visas aldrig.',
  preferNot: 'Vill inte uppge',
  pulseSubmit: 'Skicka svar',
  skipPage: 'Hoppa över',
  back: 'Tillbaka',
  declineConversation: 'Jag vill helst inte prata om det här',
  measureSkipped: 'Hoppade över',
  measureReopened: 'Ditt svar just nu är markerat. Ändra det om du vill och skicka sedan.',
  quickReplies: 'Förslag på svar',
  quickRepliesHint: 'Välj ett svar eller skriv med egna ord.',
  followUpTitle: 'En följdfråga',
  followUpContext: 'Om ”{question}”: du svarade {answer}.',
  changeAnswer: 'Ändra svaret',
  roundLabel: 'Omgång {n} · {when}',
  doneTrust: 'Så sparas och skyddas dina svar',
  skip: 'Hoppa över',
  textAnswer: 'Ditt svar (frivilligt)',
  textPlaceholder: 'Skriv här…',
};

const dictionaries: Record<string, Partial<Record<Key, string>>> = { en, sv };

export type T = (key: Key, vars?: Record<string, string | number>) => string;

export function translator(locale: string): T {
  const dict = dictionaries[locale] ?? {};
  return (key, vars = {}) => (dict[key] ?? en[key]).replace(/\{(\w+)\}/g, (m, k: string) => String(vars[k] ?? m));
}

/** Picks the text for `locale` from a localized map, falling back to the first entry. */
export const pick = (m: Record<string, string> | undefined, locale: string) => (m ? (m[locale] ?? Object.values(m)[0] ?? '') : '');

export const SUPPORTED_LOCALES = Object.keys(dictionaries);
